/**
 * The trade-in-flight gate.
 *
 * A trade is TWO chain events: the wallet's own transaction (the send — tokens
 * leave, gas is spent) and the swap. The send is confirmed first, so the wallet
 * balance moves while the swap is still executing. Measured on a real sell:
 * the send was confirmed at 1873 ms and the balance changed at 1875 ms, while
 * the swap only reached a block at 2291 ms and its row reached the browser at
 * 3025 ms. The operator saw the balance jump to an intermediate value and then
 * correct itself a second later.
 *
 * The requirement is that everything reporting "the trade succeeded" appears
 * together: the toast, the tape row, the balance, and the position cells. The
 * tape row, the toast and the position already land in one frame. This gate is
 * what brings the balance with them: while a trade is in flight, balance
 * updates are held, and the newest one is released the moment the trade
 * confirms.
 *
 * Nothing is invented — the released value is the real, latest balance. The
 * only thing that changes is WHEN it is shown: at the completed trade instead
 * of mid-flight.
 *
 * The gate is display-only. It never feeds a trade body. It is NOT true that no
 * trade is in flight at submit: clicks are accepted during a series, so the
 * shown balance can predate every in-flight debit. The pre-signing guards read
 * the shown balance minus this tab's in-flight spend (nativeSpendReserve).
 */

// A trade that never resolves must not freeze the balance. The confirmation
// path's own fail-loud deadline is 90s; this sits well inside it, so a lost
// confirmation costs a late balance, never a missing one.
const MAX_HOLD_MS = 20_000
// How long the settled floor and target may keep refusing balances AFTER the
// trade resolved. They exist to hide the window between the send block and the
// proceeds block — live records put that at 0.7-4s — not to stand forever: a
// target the real balance never hits (gas differing from the emulated figure
// by more than the fee, a sell filling away from its quote) used to hold every
// later balance indefinitely, deposits included. When this expires the newest
// held value is released: the balance can be late, never missing.
const SETTLE_TTL_MS = 5_000
// A quiet-window heuristic used to sit here. It is gone on purpose: a timer is
// a guess about when the wallet stopped moving, and this terminal's value is
// speed. The confirmation now comes from the TRADE ROW rather than from the
// seqno backstop, and by the time that row exists the wallet's own transactions
// for it have landed — so the gate opens on a fact, not on a countdown.

// F2 (stage-3): the gate holds a SET of in-flight trades. Every begin adds
// its emulator-measured delta and fee to the running sums; the settled
// target of a SERIES is base + Σdeltas with Σfees tolerance, and the floor
// is the max chain-second of the resolved trades. A ticket that never
// executed (undelivered / never sent) withdraws its contribution. With one
// trade the behavior is byte-for-byte the old one.
interface OpenTrade {
  id: number
  deltaNano: bigint | null
  toleranceNano: bigint
  holdTimer: number
}
const openTrades = new Map<number, OpenTrade>()
let nextTicketId = 1
let pendingDeltaSum = 0n
let pendingToleranceSum = 0n
/** Trades of the series whose delta still counts (a withdrawn one leaves). */
let deltaTickets = 0
let maxChainSecondMs = 0
/** A second trade began while the first was still open (see holdsBalances). */
let seriesOverlaps = false
let settleTimer = 0
/**
 * The canonical wallet address the current (or last) trade belongs to.
 *
 * The gate is a module singleton, but balance hooks exist per address: while
 * wallet A trades, wallet B's rows keep updating elsewhere. Without an owner,
 * B's snapshots were held by A's gate and — worse — consumed A's floor/target
 * or re-armed the target from B's balance. Every gate decision is scoped to
 * this owner; null (legacy callers, tests) keeps the old single-wallet
 * behavior.
 */
let ownerWallet: string | null = null
/**
 * The chain second of the confirmed trade, in ms.
 *
 * A trade moves the wallet more than once: the send leaves first and the
 * proceeds arrive after the swap. Measured on a real sell, the held push was
 * the SEND (lt …152000001, 3.5 s before the toast) and the proceeds only
 * arrived 300 and 500 ms AFTER it — so releasing the held value at the
 * confirmation put a mid-trade number on screen and corrected it twice.
 *
 * Every balance carries the chain moment it describes. With the trade's own
 * chain second, "before the trade" and "after the trade" are distinguishable by
 * a FACT rather than by waiting: the balance to show is the first one that is
 * not older than the trade itself.
 */
let settledAtLeastMs = 0
/**
 * What the wallet's TON balance is worth once the WHOLE transaction is done,
 * and how far off that the real one may land.
 *
 * A trade is several wallet events: gas and principal leave first, proceeds or
 * change arrive after. Each event carries its own balance, and showing each is
 * what the operator saw as flicker — the requirement is the balance AT THE END
 * of the transaction, once.
 *
 * The emulator measured that end before the trade was ever signed
 * (`wallet_balance_delta_nano`), so the final balance is recognisable rather
 * than guessed: it is the one that lands on that figure. The tolerance is the
 * plan's own measured network fee, because real gas differs from emulated gas
 * by less than the fee itself.
 *
 * Nothing here is ever DISPLAYED — the predicted number only tells the real
 * ones apart. If it never lands, the gate's existing ceiling releases the
 * newest anyway, so the balance can be late but never missing.
 */
let settledTargetNano: bigint | null = null
let settledToleranceNano = 0n
/** The pre-series balance the balance hook armed (F2: one base per series). */
let armedBaseNano: bigint | null = null
const listeners = new Set<() => void>()

// TEMPORARY stage-2 instrument. Lazy import would be async; the diag module is
// dependency-free, so a direct import keeps this observable synchronously.
import { displayDiagEnabled, noteDisplayEvent } from './displayWriteDiag'

function notify(): void {
  for (const listener of listeners) {
    try {
      listener()
    } catch {
      // A listener must not be able to wedge the gate.
    }
  }
}

/** Opens (or extends) the gate. Returns the trade's ticket. */
export function beginPendingTrade(settle?: {
  /** The emulator-measured signed delta this trade applies to the wallet. */
  deltaNano: string | null | undefined
  /** The plan's measured network fee, used as the tolerance. */
  networkFeeNano: string | null | undefined
  /** Canonical address of the trading wallet — the gate's owner. */
  wallet?: string | null
}): number {
  const joinsSeries = openTrades.size > 0
  if (openTrades.size === 0) {
    // A fresh series: previous sums and post-trade filters are history.
    seriesOverlaps = false
    ownerWallet = settle?.wallet ?? null
    pendingDeltaSum = 0n
    pendingToleranceSum = 0n
    deltaTickets = 0
    maxChainSecondMs = 0
    settledTargetNano = null
    settledToleranceNano = 0n
  }
  const id = nextTicketId++
  const delta = settle ? toBigIntOrNull(settle.deltaNano) : null
  const fee = settle ? toBigIntOrNull(settle.networkFeeNano) : null
  const tolerance = fee !== null && fee > 0n ? fee : 0n
  if (delta !== null) {
    pendingDeltaSum += delta
    deltaTickets += 1
  }
  pendingToleranceSum += tolerance
  const trade: OpenTrade = { id, deltaNano: delta, toleranceNano: tolerance, holdTimer: 0 }
  if (typeof setTimeout === 'function') {
    trade.holdTimer = setTimeout(() => {
      // A trade that never resolves must not freeze the balance.
      endPendingTradeTicket(id)
    }, MAX_HOLD_MS) as unknown as number
  }
  openTrades.set(id, trade)
  if (displayDiagEnabled())
    noteDisplayEvent(
      'gate',
      'begin',
      `#${id} delta=${delta ?? 'null'} open=${openTrades.size} Σdelta=${pendingDeltaSum}`,
      'pendingTradeGate',
      'broadcast'
    )
  // Listeners hear TRANSITIONS only (the pinned contract). Two exist: the
  // open, and the moment a second trade OVERLAPS the first. From then on the
  // series holds nothing — no balance hold, no floor, no target — and the
  // hook paints what it was holding at once (operator 2026-11-02: with many
  // rapid trades every balance updates instantly; the old whole-series hold
  // kept the pre-series figure on screen for the whole series).
  if (openTrades.size === 1) notify()
  else if (joinsSeries && !seriesOverlaps) {
    seriesOverlaps = true
    clearSettledBalanceFloor()
    clearSettledBalanceTarget()
    armedBaseNano = null
    notify()
  }
  return id
}

/**
 * Resolves ONE trade of the series. The gate stays held while others are in
 * flight; the LAST resolution finalizes the floor (max chain second) and the
 * settled target (base + Σdeltas of everything that executed).
 *
 * `executed: false` (undelivered / never sent) withdraws the trade's delta
 * and fee — the wallet never moved for it.
 */
export function endPendingTradeTicket(
  id: number,
  tradeChainSeconds?: number,
  executed = true
): void {
  const trade = openTrades.get(id)
  if (!trade) return
  openTrades.delete(id)
  if (trade.holdTimer && typeof clearTimeout === 'function') {
    clearTimeout(trade.holdTimer as unknown as ReturnType<typeof setTimeout>)
  }
  if (!executed) {
    if (trade.deltaNano !== null) {
      pendingDeltaSum -= trade.deltaNano
      deltaTickets -= 1
    }
    pendingToleranceSum -= trade.toleranceNano
  }
  if (
    typeof tradeChainSeconds === 'number' &&
    Number.isFinite(tradeChainSeconds) &&
    tradeChainSeconds > 0
  ) {
    maxChainSecondMs = Math.max(maxChainSecondMs, tradeChainSeconds * 1000)
  }
  if (openTrades.size > 0) {
    // Mid-series: the gate stays open, so listeners hear nothing (the pinned
    // transitions-only contract); the diag keeps the resolution observable.
    if (displayDiagEnabled())
      noteDisplayEvent(
        'gate',
        'ticket-end',
        `#${id} open=${openTrades.size}`,
        'pendingTradeGate',
        'trade-resolved'
      )
    return
  }
  finalizeSeries()
}

/**
 * Legacy close: resolves EVERY open trade at once. Kept for callers that do
 * not track tickets (tests, hold-expiry fallbacks).
 */
export function endPendingTrade(tradeChainSeconds?: number): void {
  if (openTrades.size === 0) return
  const ids = [...openTrades.keys()]
  const last = ids.pop()
  for (const id of ids) endPendingTradeTicket(id, undefined, true)
  if (last !== undefined) endPendingTradeTicket(last, tradeChainSeconds, true)
}

function finalizeSeries(): void {
  // An overlapping series arms no floor and no target: its pushes were
  // painted as they came, and a computed end over several trades is exactly
  // the number the operator saw that the wallet never had.
  settledAtLeastMs = seriesOverlaps ? 0 : maxChainSecondMs
  // The series' end state: the armed pre-trade base plus everything that
  // executed. A missing base or an all-null delta series arms no target —
  // a missing plan figure can never hold a balance back. The TTL bounds the
  // filters either way: the balance can be late, never missing.
  if (!seriesOverlaps && armedBaseNano !== null && deltaTickets > 0) {
    settledTargetNano = armedBaseNano + pendingDeltaSum
    settledToleranceNano = pendingToleranceSum
  } else {
    settledTargetNano = null
    settledToleranceNano = 0n
  }
  armedBaseNano = null
  // The previous series' still-armed window must not clear THIS series'
  // fresh floor/target early (review T5: back-to-back series inside 5s are
  // routine, and the stale timer's guards cannot tell series apart).
  if (settleTimer && typeof clearTimeout === 'function') {
    clearTimeout(settleTimer as unknown as ReturnType<typeof setTimeout>)
    settleTimer = 0
  }
  if ((settledAtLeastMs > 0 || settledTargetNano !== null) && typeof setTimeout === 'function') {
    settleTimer = setTimeout(expireSettleWindow, SETTLE_TTL_MS) as unknown as number
  }
  if (displayDiagEnabled())
    noteDisplayEvent(
      'gate',
      'end',
      `floor=${settledAtLeastMs} target=${settledTargetNano ?? 'null'}`,
      'pendingTradeGate',
      'trade-resolved'
    )
  notify()
}

function expireSettleWindow(): void {
  settleTimer = 0
  if (openTrades.size > 0) return
  if (settledAtLeastMs === 0 && settledTargetNano === null) return
  clearSettledBalanceFloor()
  clearSettledBalanceTarget()
  if (displayDiagEnabled())
    noteDisplayEvent('gate', 'settle-ttl', 'expired', 'pendingTradeGate', 'timer')
  notify()
}

/**
 * The chain instant a balance must reach to be the trade's OUTCOME rather than
 * a state inside it. 0 when nothing is pending that answer.
 */
export function settledBalanceFloorMs(): number {
  return settledAtLeastMs
}

/**
 * Is this balance the transaction's END, rather than a state inside it?
 *
 * True when no target was measured, so a missing plan figure can never hold a
 * balance back.
 */
/** Emulator-measured end-of-trade native balance. Display-only stand-in after confirm. */
export function settledBalanceTargetNano(): bigint | null {
  return settledTargetNano
}

export function isSettledBalance(balanceNano: string | null | undefined): boolean {
  if (settledTargetNano === null) return true
  const value = toBigIntOrNull(balanceNano)
  if (value === null) return true
  const diff = value > settledTargetNano ? value - settledTargetNano : settledTargetNano - value
  return diff <= settledToleranceNano
}

/**
 * The balance the wallet had before the trade. Only the balance hook knows it
 * for certain, so it arms the target rather than the execution path guessing.
 */
export function armSettledBalanceTarget(preTradeBalanceNano: string | null | undefined): void {
  // F2: the base arms ONCE per series (the first held balance is the true
  // pre-trade one); the target itself is computed at finalize as
  // base + Σdeltas of everything that executed.
  if (openTrades.size === 0 || deltaTickets === 0) return
  const base = toBigIntOrNull(preTradeBalanceNano)
  if (base === null) return
  if (armedBaseNano === null) armedBaseNano = base
  if (displayDiagEnabled())
    noteDisplayEvent(
      'gate',
      'arm-target',
      `base=${armedBaseNano} Σdelta=${pendingDeltaSum}`,
      'pendingTradeGate',
      'gate-open'
    )
}

/** The target is spent once the settled balance has been shown. */
export function clearSettledBalanceTarget(): void {
  settledTargetNano = null
  settledToleranceNano = 0n
}

function toBigIntOrNull(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '' || !/^-?\d+$/.test(trimmed)) return null
  try {
    return BigInt(trimmed)
  } catch {
    return null
  }
}

/** The floor is spent once a qualifying balance has been shown. */
export function clearSettledBalanceFloor(): void {
  settledAtLeastMs = 0
}

function clearTimers(): void {
  if (typeof clearTimeout !== 'function') return
  for (const trade of openTrades.values()) {
    if (trade.holdTimer) clearTimeout(trade.holdTimer as unknown as ReturnType<typeof setTimeout>)
    trade.holdTimer = 0
  }
  if (settleTimer) clearTimeout(settleTimer as unknown as ReturnType<typeof setTimeout>)
  settleTimer = 0
}

export function isPendingTrade(): boolean {
  return openTrades.size > 0
}

/**
 * Should the balance hook HOLD pushes right now? Only while exactly one trade
 * is in flight and no other overlapped it: the send → proceeds of ONE trade.
 * A series of overlapping trades holds nothing (isPendingTrade still answers
 * for the position freeze, which is a different question).
 */
export function holdsBalances(): boolean {
  return openTrades.size > 0 && !seriesOverlaps
}

/**
 * The wallet whose trade the gate (and its post-trade floor/target) belongs
 * to. null = unscoped (legacy callers): every wallet is treated as the owner.
 */
export function pendingTradeOwner(): string | null {
  return ownerWallet
}

/** Subscribes to gate transitions. Returns the unsubscribe. */
export function subscribePendingTrade(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam. */
export function resetPendingTradeGateForTest(): void {
  clearTimers()
  openTrades.clear()
  pendingDeltaSum = 0n
  pendingToleranceSum = 0n
  deltaTickets = 0
  maxChainSecondMs = 0
  seriesOverlaps = false
  armedBaseNano = null
  ownerWallet = null
  settledAtLeastMs = 0
  settledTargetNano = null
  settledToleranceNano = 0n
  listeners.clear()
}
